import { FormHelperText, Skeleton, Stack } from "@mui/material";
import { DateFieldProps, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Controller, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { calendarLocalization } from "../common/utils/calendar";
import FormFieldHint from "./FormFieldHint";
// Required for localization.
import "dayjs/locale/it";
import "dayjs/locale/en";

interface Props<T extends FieldValues> extends Omit<DateFieldProps<any>, "name" | "label" | "control"> {
	name: Path<T>;
	label: string;
	readOnly?: boolean;
	required?: boolean;
	isLoading?: boolean;
	hintMessage?: string;
	afterChange?: () => void;
	form: UseFormReturn<T, null>;
	size?: "small" | "medium";
}

/**
 * FormDateField acts as a wrapper component for date fields used in conjunction with React Hook Form.
 *
 * @param name        the name of the field bound to this date field
 * @param label       the label of the date field
 * @param readOnly    if true, makes the field read-only
 * @param required    if defined, the label will be rendered marked with "*"
 * @param isLoading   if true, the component will render a skeleton element
 * @param hintMessage the optional hint message that may be rendered at the end of the field
 * @param form        the form reference
 * @param afterChange if defined, this callback is invoked right after internal onChange event
 * @param rest
 * @returns           a DatePicker wrapped in a React Hook Form Control component
 */
const FormDateField = <T extends FieldValues>({
	form,
	name,
	label,
	readOnly,
	required,
	isLoading,
	hintMessage,
	afterChange,
	size,
	...rest
}: Props<T>): React.ReactElement => {
	const { i18n } = useTranslation();
	const localization = calendarLocalization(i18n.language);

	// Show "*" right after mandatory fields (i.e. those to which validation rules are applied).
	label = required ? `${label} *` : label;

	// Set date locale.
	dayjs.locale(i18n.language);

	if (isLoading) {
		return <Skeleton height={"60px"} variant="rounded" className="loading-field" />;
	}

	return (
		<Controller
			name={name}
			control={form.control}
			render={({ field: { onChange, value }, fieldState: { error } }) => (
				<Stack spacing={1} direction="column">
					<Stack spacing={1} direction="row">
						<LocalizationProvider
							dateAdapter={AdapterDayjs}
							localeText={localization}
							adapterLocale={i18n.language}
						>
							<DatePicker
								{...rest}
								value={value ?? null}
								label={label}
								readOnly={readOnly}
								className={readOnly ? "read-only" : undefined}
								slotProps={{
									actionBar: { actions: ["clear", "accept", "today"] },
									textField: { size: size ?? "medium" },
								}}
								sx={{ width: rest.fullWidth ? "100%" : undefined }}
								onChange={(v) => {
									onChange(v ?? null);
									afterChange?.();
									form.trigger(name);
								}}
							/>
						</LocalizationProvider>
					</Stack>
					<FormHelperText component="div" error={error !== undefined}>
						{error?.message ?? <FormFieldHint text={hintMessage} />}
					</FormHelperText>
				</Stack>
			)}
		/>
	);
};

export default FormDateField;
